import { createFileRoute, Link } from "@tanstack/react-router";
import { breadcrumbJsonLd } from "@/lib/breadcrumb-jsonld";
import { serviceJsonLd } from "@/lib/service-jsonld";
import { groepenkastProductJsonLd } from "@/lib/groepenkast-product-jsonld";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { CITY_CONTENT, GENERIC_FAQ } from "@/lib/groepenkast-cities";
import { ShieldCheck, Clock, CheckCircle2, MapPin, ArrowRight, Star } from "lucide-react";
import { GOOGLE_REVIEW_SCHRIJVEN_URL } from "@/data/reviews";

const SITE_URL = "https://elvion-group.nl";

const KOSTEN_FACTOREN = [
  { titel: "Aantal groepen", tekst: "Hoe meer groepen (verlichting, stopcontacten, apparaten), hoe groter de kast. Boven 24 modules komt er een tweede kast bij." },
  { titel: "1-fase of 3-fase", tekst: "Een 3-fase kast is nodig bij zware verbruikers zoals inductie, een warmtepomp of laadpaal, en vraagt andere componenten." },
  { titel: "Zware verbruikers", tekst: "Een kookgroep, laadpaal of warmtepomp krijgt een eigen groep en soms een extra aardlekschakelaar." },
  { titel: "Staat van de meterkast", tekst: "Oude bedrading of een verouderde stoppenkast kan extra werk vragen. Dat ziet u vooraf terug in de offerte." },
  { titel: "Voorbereiding op de toekomst", tekst: "Extra ruimte voor zonnepanelen of een laadpaal nu meenemen is voordeliger dan later bijplaatsen." },
  { titel: "Herverzegeling", tekst: "Wij zijn zegelbevoegd bij Liander, Stedin en Enexis, dus u betaalt geen aparte kosten voor herverzegeling." },
];

const WERKWIJZE = [
  { titel: "Aanvraag", tekst: "Stel uw kast samen in de configurator of vraag online een offerte aan." },
  { titel: "Offerte binnen 24 uur", tekst: "U ontvangt een vaste prijs vooraf, afgestemd op uw meterkast en wensen." },
  { titel: "Montage", tekst: "Na uw akkoord plaatsen we de nieuwe AEG-kast, meestal binnen een week. U bent maar kort zonder stroom." },
  { titel: "Keuring en garantie", tekst: "U krijgt een keuringsrapport volgens NEN 1010, 15 jaar garantie op AEG en 2 jaar op ons werk." },
];

const HUB_FAQ = [
  { q: "Wat kost het vervangen van een groepenkast?", a: "Dat hangt af van het aantal groepen, 1-fase of 3-fase en de staat van uw meterkast. U ontvangt vooraf een vaste offerte op maat, zonder verrassingen achteraf." },
  ...GENERIC_FAQ.slice(1),
];

export const Route = createFileRoute("/groepenkast-vervangen/")({
  head: () => ({
    meta: [
      { title: "Groepenkast vervangen: kosten, werkwijze en vragen | Elvion" },
      {
        name: "description",
        content:
          "Wat kost een groepenkast vervangen en hoe gaat het in zijn werk? Vaste prijs vooraf, offerte binnen 24 uur, montage binnen een week en 15 jaar garantie op AEG.",
      },
      { property: "og:title", content: "Groepenkast vervangen | Elvion Group Nederland" },
      {
        property: "og:description",
        content:
          "Vind uw lokale installateur voor het vervangen van uw groepenkast. Actief in Amsterdam, Rotterdam, Utrecht en meer.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE_URL}/groepenkast-vervangen` },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Groepenkast vervangen | Elvion Group Nederland" },
      { name: "twitter:description", content: "Erkende installateurs, vaste prijs vooraf en 15 jaar garantie op AEG-groepenkasten." },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/groepenkast-vervangen` }],
    scripts: [
      breadcrumbJsonLd([{ name: "Groepenkast vervangen", path: "/groepenkast-vervangen" }]),
      serviceJsonLd({
        path: "/groepenkast-vervangen",
        name: "Groepenkast vervangen door Elvion Group Nederland",
        description:
          "Vervanging van uw groepenkast door erkende NEN 1010-installateurs. Vaste prijs vooraf, AEG-materialen met 15 jaar garantie en offerte binnen 24 uur — landelijke dekking.",
        serviceType: "Groepenkast vervangen",
        offerCatalog: [
          "Groepenkast vervangen 1-fase",
          "Groepenkast vervangen 3-fase",
          "Uitbreiding groepenkast",
          "Aardlekautomaten plaatsen",
          "Meterkast ombouwen",
          "Advies en keuring NEN 1010",
        ],
      }),
      groepenkastProductJsonLd({
        url: `${SITE_URL}/groepenkast-vervangen`,
        name: "Groepenkast vervangen",
        description:
          "Vervanging van uw groepenkast door erkende NEN 1010-installateurs met AEG-materialen en 15 jaar garantie.",
      }),
    ],
  }),
  component: GroepenkastHub,
});

function GroepenkastHub() {
  const cities = Object.entries(CITY_CONTENT).map(([slug, city]) => ({
    slug,
    name: city.name,
    neighborhoods: city.neighborhoods.slice(0, 4),
  }));

  return (
    <div className="min-h-screen bg-white">
      <Header variant="shop" />
      <main>
        <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <nav aria-label="Kruimelpad" className="mb-4 text-sm text-text-secondary">
            <Link to="/" className="hover:text-orange">Home</Link>
            <span className="mx-2 text-foreground/30">/</span>
            <span className="text-foreground">Groepenkast vervangen</span>
          </nav>

          <div className="max-w-3xl">
            <h1 className="font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
              Groepenkast vervangen door een erkende installateur
            </h1>
            <p className="mt-4 text-base text-text-secondary sm:text-lg">
              Elvion Group Nederland vervangt dagelijks groepenkasten in het hele land. Alles volgens
              NEN 1010, zegelbevoegd bij Liander, Stedin en Enexis en met 15 jaar garantie op AEG.
              Kies hieronder uw stad voor lokale prijzen, een lokale installateur en klantverhalen.
            </p>

            <ul className="mt-6 grid gap-2 sm:grid-cols-2">
              <li className="flex items-start gap-2 text-sm text-foreground/80">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-orange" />
                <span>15 jaar garantie op AEG-groepenkasten</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground/80">
                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-orange" />
                <span>Offerte binnen 24 uur, montage binnen 1 week</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground/80">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-orange" />
                <span>Vaste prijs vooraf, geen meerwerk</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground/80">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-orange" />
                <span>Zegelbevoegd bij Liander, Stedin en Enexis</span>
              </li>
            </ul>
          </div>
        </section>

        <section aria-labelledby="steden-heading" className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
          <h2 id="steden-heading" className="font-display text-2xl font-bold text-foreground sm:text-3xl">
            Kies uw stad
          </h2>
          <p className="mt-2 text-text-secondary">
            Klik op een stad voor lokale informatie, prijzen en de vaste installateur uit uw regio.
          </p>

          <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {cities.map((city) => (
              <li key={city.slug}>
                <Link
                  to="/groepenkast-vervangen/$stad"
                  params={{ stad: city.slug }}
                  className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-orange hover:shadow-md"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-lg font-bold text-foreground group-hover:text-orange">
                      Groepenkast vervangen {city.name}
                    </h3>
                    <ArrowRight className="h-5 w-5 text-slate-400 transition group-hover:text-orange" />
                  </div>
                  <p className="mt-2 text-sm text-text-secondary">
                    Actief in {city.neighborhoods.join(", ")} en omgeving.
                  </p>
                </Link>
              </li>
            ))}
          </ul>

          <div className="mt-10 rounded-2xl bg-slate-50 p-6 text-sm text-text-secondary">
            Uw stad staat er niet tussen? Bekijk onze{" "}
            <Link to="/dekking" className="font-semibold text-orange hover:underline">
              volledige dekking
            </Link>{" "}
            of vraag direct een{" "}
            <Link to="/offerte-aanvragen" search={{ product: "groepenkast" }} className="font-semibold text-orange hover:underline">
              offerte voor uw groepenkast
            </Link>{" "}
            aan. Interesse in complete sets? Bekijk onze{" "}
            <Link to="/aanbiedingen" className="font-semibold text-orange hover:underline">
              actuele aanbiedingen
            </Link>
            .
          </div>
        </section>

        <section aria-labelledby="kosten-heading" className="bg-slate-50">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <h2 id="kosten-heading" className="font-display text-2xl font-bold text-foreground sm:text-3xl">
              Wat kost een groepenkast vervangen?
            </h2>
            <p className="mt-3 max-w-3xl text-text-secondary">
              Elke meterkast is anders, daarom ontvangt u altijd eerst een vaste offerte op maat, zonder verrassingen
              achteraf. De prijs hangt vooral af van deze punten:
            </p>
            <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {KOSTEN_FACTOREN.map((f) => (
                <li key={f.titel} className="rounded-2xl border border-slate-200 bg-white p-5">
                  <h3 className="font-display text-base font-bold text-foreground">{f.titel}</h3>
                  <p className="mt-2 text-sm text-text-secondary">{f.tekst}</p>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-text-secondary">
              Inbegrepen: AEG-materialen, montage, keuringsrapport volgens NEN 1010 en herverzegeling bij uw netbeheerder.
              Meerwerk gebeurt alleen na uw akkoord.{" "}
              <Link to="/groepenkast-configurator" className="font-semibold text-orange hover:underline">
                Stel uw kast samen
              </Link>{" "}
              of{" "}
              <Link to="/offerte-aanvragen" search={{ product: "groepenkast" }} className="font-semibold text-orange hover:underline">
                vraag direct een offerte aan
              </Link>
              .
            </p>
          </div>
        </section>

        <section aria-labelledby="werkwijze-heading" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <h2 id="werkwijze-heading" className="font-display text-2xl font-bold text-foreground sm:text-3xl">
            Zo werkt het
          </h2>
          <ol className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {WERKWIJZE.map((s, i) => (
              <li key={s.titel} className="rounded-2xl border border-slate-200 bg-white p-5">
                <span className="font-mono text-sm font-bold text-orange">Stap {i + 1}</span>
                <h3 className="mt-1 font-display text-base font-bold text-foreground">{s.titel}</h3>
                <p className="mt-2 text-sm text-text-secondary">{s.tekst}</p>
              </li>
            ))}
          </ol>
        </section>

        <section id="review" aria-labelledby="review-heading" className="mx-auto max-w-3xl px-4 py-12 text-center sm:px-6 lg:px-8">
          <h2 id="review-heading" className="font-display text-2xl font-bold text-foreground sm:text-3xl">
            Groepenkast door ons laten vervangen?
          </h2>
          <p className="mt-3 text-text-secondary">
            Bent u tevreden over ons werk? Laat een review achter op Google, daarmee helpt u andere klanten.
          </p>
          <a
            href={GOOGLE_REVIEW_SCHRIJVEN_URL}
            target="_blank"
            rel="noopener"
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 font-semibold text-primary-foreground hover:opacity-90"
          >
            <Star className="h-5 w-5" aria-hidden="true" /> Schrijf een Google-review
          </a>
        </section>

        <section aria-labelledby="faq-heading" className="bg-slate-50">
          <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
            <h2 id="faq-heading" className="font-display text-2xl font-bold text-foreground sm:text-3xl">
              Veelgestelde vragen over groepenkast vervangen
            </h2>
            <div className="mt-8 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
              {HUB_FAQ.map((f) => (
                <details key={f.q} className="group p-5">
                  <summary className="cursor-pointer list-none font-semibold text-foreground marker:hidden">
                    {f.q}
                  </summary>
                  <p className="mt-3 text-sm text-text-secondary">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
